import {
  Button,
  Combobox,
  ErrorContainer,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Heading,
  IconBin,
  IconPrimaryButtonArrow,
  IconRefresh,
  ScrollArea,
} from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useUIStateStore } from "@/store/uiStateStore";

import type { OpportunitySongItem } from "./types/opportunities.types";

import { useOpportunitiesApplyForm } from "./hooks/useOpportunitiesApplyForm";
import { useOpportunitiesApplyMutation } from "./hooks/useOpportunitiesApplyMutation";

function OpportunitiesApply() {
  const { t } = useTranslation();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const navigate = useNavigate();

  const {
    form,
    myAccount,
    userInfo,
    oppsData,
    isSuccess,
    noOpportunities,
    pending,
    setPending,
    mutationErrorMessage,
    setMutationErrorMessage,
    options,
    cachedSongs,
    handleInputChange,
  } = useOpportunitiesApplyForm();

  const { applyOpportunity } = useOpportunitiesApplyMutation({
    form,
    cachedSongs,
    oppsData,
    myAccount,
    userInfo,
    setPending,
    setMutationErrorMessage,
  });

  useEffect(() => {
    if (noOpportunities === true) {
      void navigate({ to: "/" });
    }
  }, [noOpportunities, navigate]);

  return (
    <div className="flex-1 bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      <Heading as="h3" className="mb-8">{t("other:apply", "Apply")}</Heading>
      <div className="bg-white shadow-sm rounded-lg p-4 md:px-14 md:py-10">

        {isSuccess && (
          <div className="w-full">
            <Form {...form}>
              <form
                className="flex flex-col gap-6"
                onSubmit={(e) => { void form.handleSubmit(applyOpportunity)(e); }}
              >
                <div className="flex flex-col gap-2">
                  <Heading as="h4">{t("other:which-opportunity-header", "Which opportunity are you applying for?")}</Heading>
                  <FormField
                    control={form.control}
                    name="opp"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder={t("other:select-opportunity-placeholder", "Select opportunity") ?? "Select opportunity"}
                            searchByLabel
                            data={oppsData.map(opp => ({
                              value: opp._id,
                              label: opp.name,
                            }))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Heading as="h4">{t("other:select-five-songs-header", "Select at least 5 songs")}</Heading>
                  <FormField
                    control={form.control}
                    name="songList"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            customWidth
                            triggerClassName="w-full max-w-none"
                            multiSelectDisplay="count"
                            placeholder={t("other:select-a-song-placeholder", "Select a song") ?? "Select a song"}
                            values={field.value.map((s: OpportunitySongItem) => s.workcode)}
                            onValuesChange={(workcodes) => {
                              field.onChange(
                                workcodes.map((wc) => {
                                  const existing = field.value.find(
                                    (s: OpportunitySongItem) => s.workcode === wc,
                                  );
                                  if (existing !== undefined)
                                    return existing;
                                  const cached = cachedSongs.find(s => s.workcode === wc);
                                  return {
                                    workcode: wc,
                                    title: cached?.title ?? "",
                                    performingartists: cached?.performingartists ?? "",
                                    label: cached !== undefined
                                      ? `${cached.title} - (${cached.performingartists})`
                                      : wc,
                                  };
                                }),
                              );
                            }}
                            data={options}
                            onSearchChange={handleInputChange}
                          />
                        </FormControl>
                        {field.value.length > 0 && (
                          <ScrollArea className="mt-2 h-48 rounded-lg border border-stroke-subtle">
                            <div className="flex flex-col gap-2 p-1">
                              {field.value.map((song: OpportunitySongItem) => (
                                <div
                                  key={song.workcode}
                                  className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm"
                                >
                                  <span className="font-semibold">{song.label ?? song.title}</span>
                                  <Button
                                    type="button"
                                    variant="link"
                                    aria-label={t("common:remove", "Remove") ?? "Remove"}
                                    icon={(
                                      <IconBin className="w-5 h-5 stroke-ink-secondary hover:stroke-ink-error" />
                                    )}
                                    onClick={() => {
                                      field.onChange(
                                        field.value.filter(
                                          (s: OpportunitySongItem) => s.workcode !== song.workcode,
                                        ),
                                      );
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Heading as="h4">{t("other:tell-us-more-header", "Tell us more about you")}</Heading>
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <textarea
                            id="txtOpportunityEvidence"
                            placeholder={t("other:supporting-text-placeholder", "Supporting text") ?? "Supporting text"}
                            rows={4}
                            className="w-full font-sentric-alt font-medium text-sm rounded-lg px-2 py-1.5 ring-1 ring-canvas-inverse hover:ring-2 hover:ring-stroke-primary focus-visible:ring-2 focus-visible:ring-stroke-primary focus-visible:outline-none placeholder:text-black/50 transition ease-out duration-200 resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-3">
                  {mutationErrorMessage.length > 0 && (
                    <ErrorContainer message={mutationErrorMessage} />
                  )}
                  {form.formState.errors.root?.message != null && (
                    <ErrorContainer message={form.formState.errors.root.message} />
                  )}
                  <div className="flex justify-end">
                    <Button
                      id="btnOpportunitySubmit"
                      type="submit"
                      variant={isTunecore ? "secondary" : "primary"}
                      disabled={pending}
                      label={t("common:submit-button", "Submit")}
                      icon={
                        pending
                          ? <IconRefresh className="animate-spin" />
                          : <IconPrimaryButtonArrow />
                      }
                    />
                  </div>
                </div>
              </form>
            </Form>
          </div>
        )}
      </div>
    </div>
  );
}

export default OpportunitiesApply;
